import { useState, useCallback } from "react";
import type { ReactNode, ChangeEvent } from "react";
import { CheckmarkCircleFilled, DiamondDismiss12Filled } from "@fluentui/react-icons";
import type { IPartnerInfo } from "@/types/partnerTypes";
import { partnerService } from "@/services/partnerService";
import styles from "./NewRequestDialog.module.scss";

interface IPartnerLookupProps {
  partner: IPartnerInfo | null;
  onPartnerFound: (partner: IPartnerInfo) => void;
  onPartnerCleared: () => void;
  onSearchError: (message: string) => void;
  searchError: string | null;
}

export function PartnerLookup({
  partner,
  onPartnerFound,
  onPartnerCleared,
  onSearchError,
  searchError,
}: IPartnerLookupProps): ReactNode {
  const [partnerOneId, setPartnerOneId] = useState<string>("");
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const isSearchDisabled = partnerOneId.trim().length === 0 || isSearching || hasSearched;

  const handlePartnerIdChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value.replace(/\D/g, "");
      setPartnerOneId(value);
      setHasSearched(false);
      if (partner || searchError) {
        onPartnerCleared();
      }
    },
    [partner, searchError, onPartnerCleared],
  );

  const handleSearch = useCallback(async (): Promise<void> => {
    if (isSearchDisabled) {
      return;
    }

    setIsSearching(true);
    setHasSearched(true);

    try {
      const result = await partnerService.lookupPartner(partnerOneId.trim());
      onPartnerFound(result);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "An error occurred during partner lookup.";
      onSearchError(message);
    } finally {
      setIsSearching(false);
    }
  }, [partnerOneId, isSearchDisabled, onPartnerFound, onSearchError]);

  return (
    <>
      {/* Partner lookup row */}
      <div className={styles.lookupRow}>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel} htmlFor="dialog-partnerOneId">
            PartnerOne ID
          </label>
          <input
            id="dialog-partnerOneId"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={20}
            className={styles.input}
            placeholder="ex. 12345678"
            value={partnerOneId}
            onChange={handlePartnerIdChange}
            aria-describedby={searchError ? "dialog-search-error" : undefined}
          />
        </div>
        <button
          type="button"
          className={`${styles.actionButton} ${!isSearchDisabled ? styles.actionButtonEnabled : ""}`}
          disabled={isSearchDisabled}
          onClick={handleSearch}
          aria-label="Check partner"
        >
          {isSearching ? "Checking..." : "Check"}
        </button>
      </div>

      {/* Status indicator — partner found */}
      {partner && !searchError && (
        <div className={styles.statusIndicator} role="status" aria-live="polite">
          <span className={styles.statusIconSuccess} aria-hidden="true">
            <CheckmarkCircleFilled />
          </span>
          <span className={styles.statusText}>
            {`Partner ${partner.partnerName} found!`}
          </span>
        </div>
      )}

      {/* Status indicator — partner not found */}
      {searchError && (
        <div className={styles.statusIndicator} role="alert">
          <span className={styles.statusIconError} aria-hidden="true">
            <DiamondDismiss12Filled />
          </span>
          <span id="dialog-search-error" className={styles.statusText}>
            {searchError}
          </span>
        </div>
      )}
    </>
  );
}
